import { ANALYTICS_EVENTS } from '@syncanix/shared';
import { ArrowLeft, Ban, FileDown } from 'lucide-react';
import { useEffect, useState } from 'react';

import { track } from '@/analytics/track';
import { CtaArrow, ctaClasses, ACTION_LINK } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { PageHero, Reveal, SectionHeader } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import {
  BillingToggle,
  CARD_TIERS,
  NO_FREE_TIER_NOTE_KEY,
  type BillingPeriod,
} from '@/features/pricing-card';
import { TierCard } from '@/features/pricing-card/TierCard';
import { PricingFeatureMatrix } from '@/features/pricing-feature-matrix';
import { UcCalculator } from '@/features/uc-calculator';
import { UcExplainer } from '@/features/uc-explainer';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Marketing `/pricing` page (Phase 8 Pass 1 iter 334, AF-01).
 *
 * Closes the UX gap where Hero / Features / PricingSection all linked
 * to `/pricing` and the route didn't exist (visitor → 404). Page
 * reuses the iter-267 `PRICING_TIERS` SOT so this page + the home
 * `PricingSection` never drift.
 *
 * Layout:
 *   - PageHero: h1 + subtext
 *   - UcExplainer: usage-based value band — published per-action UC weights +
 *     a worked example + the no-surprise-bills promise (replaces the AF-269
 *     "horror story" hero, which self-owned the Enterprise outcome axis)
 *   - Tier grid: the four headline tiers (Starter … Enterprise) via TierCard +
 *     billing toggle + risk-reversal strip + no-free-tier / caps / UC-gloss
 *     notes
 *   - UcCalculator: interactions/mo → estimated UC → recommended tier
 *   - PricingFeatureMatrix: full per-tier feature comparison
 *   - FAQ + footer nav (back home · build-vs-buy · see all features)
 *
 * The Managed/BYOK tier×route grid (`PricingMatrix`) was removed — it
 * duplicated the cards and over-indexed on BYOK; BYOK now lives as one line in
 * the UcExplainer + the BYOK FAQ.
 *
 * Per `.claude/rules/ui-design.md`: WCAG 2.1 AA (single main, labelled
 * list + region, focus-visible CTAs); RTL guardrail: logical Tailwind
 * directionals only (no `left-*`/`right-*`).
 */

interface FaqEntry {
  readonly id: string;
  readonly questionKey: string;
  readonly answerKey: string;
}

/**
 * Risk-reversal strip under the tier grid. Each item is factual per ADR-0149
 * (hard-stop billing by default) + the cancel/export promise on the refunds +
 * FAQ surfaces (`pricing.faq.cancel.a` — catalog stays exportable, no lock-in).
 *
 * ADR-0489 removed the "14-day trial — no card to start" item rather than
 * replacing it. There is no automatic trial any more, and no equally-true
 * low-risk-to-start claim to put in its place; inventing one to keep the strip
 * at three would be the exact dishonesty this file's "each item is factual"
 * rule exists to prevent. Two true claims beat three with a filler.
 */
const RISK_REVERSAL: readonly { id: string; Icon: typeof Ban; key: string }[] = [
  { id: 'cancel', Icon: FileDown, key: 'pricing.riskReversal.cancel' },
  { id: 'hardStop', Icon: Ban, key: 'pricing.riskReversal.hardStop' },
];

const FAQ: readonly FaqEntry[] = [
  {
    id: 'usage-credits',
    questionKey: 'pricing.faq.usageCredits.q',
    answerKey: 'pricing.faq.usageCredits.a',
  },
  { id: 'overage', questionKey: 'pricing.faq.overage.q', answerKey: 'pricing.faq.overage.a' },
  { id: 'byok', questionKey: 'pricing.faq.byok.q', answerKey: 'pricing.faq.byok.a' },
  { id: 'annual', questionKey: 'pricing.faq.annual.q', answerKey: 'pricing.faq.annual.a' },
  { id: 'cancel', questionKey: 'pricing.faq.cancel.q', answerKey: 'pricing.faq.cancel.a' },
  {
    id: 'enterprise',
    questionKey: 'pricing.faq.enterprise.q',
    answerKey: 'pricing.faq.enterprise.a',
  },
  {
    id: 'resolution',
    questionKey: 'pricing.faq.resolution.q',
    answerKey: 'pricing.faq.resolution.a',
  },
];

export function Pricing(): ReactElement {
  const { t } = useT();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const description = t('pricing.page.description');
  useLocalizedSeoMeta({
    title: t('pricing.page.title'),
    description,
    path: '/pricing',
    // FAQPage structured data from the same on-page Q&A, resolved through the
    // active translator (EN baseline at prerender time).
    faq: FAQ.map((e) => ({ question: t(e.questionKey), answer: t(e.answerKey) })),
  });

  // The real pricing page view (richer than $pageview for the funnel).
  useEffect(() => {
    track(ANALYTICS_EVENTS.MARKETING_PRICING_VIEWED);
  }, []);

  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      <PageHero title={t('pricing.page.heading')} description={description} />

      <UcExplainer />

      {/* The page's most important section had no heading — only an
          `aria-label` — so a sighted reader arrived at four price cards under a
          bare toggle, after 232px of empty page where the two bands' vertical
          rhythm met. Titling it fills the void with the one thing that belongs
          there, and the copy is the SAME pair the landing's pricing section
          uses, so the two cannot drift into describing different offers. */}
      <section
        aria-labelledby="pricing-tiers-heading"
        className="border-b border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-6 py-16 sm:py-20"
      >
        <div className="mx-auto max-w-6xl">
          <h2
            id="pricing-tiers-heading"
            className="text-balance text-center text-[clamp(1.7rem,3vw,2.25rem)] font-semibold tracking-[-0.04em] text-[color:var(--bd-ink)]"
          >
            {t('pricing.section.heading')}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-center text-[1.02rem] leading-[1.62] text-[color:var(--bd-body)]">
            {t('pricing.section.subhead')}
          </p>
          <div className="mb-8 mt-9 flex justify-center">
            <BillingToggle value={billingPeriod} onChange={setBillingPeriod} />
          </div>
          <ul
            aria-label={t('pricing.tiers.listLabel')}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {CARD_TIERS.map((tier) => (
              <TierCard key={tier.slug} tier={tier} billingPeriod={billingPeriod} />
            ))}
          </ul>
          {/* Risk-reversal strip — factual per ADR-0149 + the cancel/export
              promise. Sits directly under the tier grid. */}
          <ul
            aria-label={t('pricing.riskReversal.regionLabel')}
            className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3"
          >
            {RISK_REVERSAL.map(({ id, Icon, key }) => (
              <li
                key={id}
                data-risk={id}
                className="flex items-center gap-3 rounded-lg border border-[color:var(--bd-line)] bg-[color:var(--bd-page)] p-4 text-sm text-[color:var(--bd-muted)]"
              >
                <Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-[color:var(--bd-ink)]" />
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
          <p
            aria-label={t('pricing.noFreeTierNote.label')}
            className="mx-auto mt-12 max-w-2xl text-center text-sm text-[color:var(--bd-muted)]"
          >
            {t(NO_FREE_TIER_NOTE_KEY)}
          </p>
          {/* ADR-0149 — the no-bill-cliffs promise + opt-in pay-as-you-go
              overage pricing, published right under the included-UC note it
              explains. */}
          <p
            data-caps-note="true"
            className="mx-auto mt-3 max-w-2xl text-center text-sm text-[color:var(--bd-muted)]"
          >
            {t('pricing.capsNote')}
          </p>
          {/* B1c — UC (usage credits) glossed where it first appears (the
              cards), not five screens later in the FAQ. */}
          <p
            data-allowance-gloss="true"
            className="mx-auto mt-3 max-w-2xl text-center text-xs text-[color:var(--bd-muted)]"
          >
            {t('pricing.usageCreditsGloss')}
          </p>
        </div>
      </section>

      {/* Usage calculator: interactions/mo → estimated UC → recommended tier +
          $/mo, with the hard-stop / opt-in-overage overflow rule (ADR-0149). */}
      <UcCalculator />

      {/* Full per-tier feature-comparison matrix — every numeric cell is
          SOT-drift-guarded (feature-matrix-data.ts). */}
      <PricingFeatureMatrix />

      <section
        aria-labelledby="faq-heading"
        className="border-b border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-6 py-16 sm:py-20"
      >
        <div className="mx-auto max-w-5xl">
          <SectionHeader id="faq-heading" align="start">
            {t('pricing.faq.heading')}
          </SectionHeader>
          {/* Two columns from `md` up — question in the inline-start rail, answer
              flowing beside it — over hairline dividers. The same reference-table
              idiom the docs use for a code definition list, and for the same
              reason: nine stacked question/answer pairs on a `gap` alone have no
              row boundary, so a bold question reads as a heading with a
              paragraph under it and the section becomes a wall. It stacks below
              `md`, where an 18rem question rail would leave the answer a few
              words wide. */}
          <dl className="mt-9 flex flex-col">
            {FAQ.map((entry, i) => (
              <Reveal
                key={entry.id}
                data-faq={entry.id}
                className={`py-6 first:pt-0 md:flex md:gap-10 ${
                  i > 0 ? 'border-t border-[color:var(--bd-line)]' : ''
                }`}
                delayMs={Math.min(i, 4) * 60}
              >
                <dt className="text-balance text-base font-semibold tracking-[-0.022em] text-[color:var(--bd-ink)] md:w-[18rem] md:shrink-0 md:text-[1.02rem]">
                  {t(entry.questionKey)}
                </dt>
                <dd className="mt-2 text-pretty text-[0.94rem] leading-[1.66] text-[color:var(--bd-body)] md:mt-0 md:flex-1">
                  {t(entry.answerKey)}
                </dd>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      <section
        aria-label={t('pricing.footerNav.regionLabel')}
        className="border-t border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-6 py-12"
      >
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center sm:flex-row sm:justify-between">
          <Link to="/" className={`inline-flex items-center gap-1 text-sm ${ACTION_LINK}`}>
            <ArrowLeft aria-hidden="true" className="h-4 w-4 rtl:rotate-180" />{' '}
            {t('pricing.footerNav.backHome')}
          </Link>
          <Link
            to="/pricing/build-vs-buy"
            className={`inline-flex items-center gap-1 text-sm ${ACTION_LINK}`}
          >
            {t('pricing.footerNav.buildVsBuy')}
          </Link>
          <Link to="/features" className={ctaClasses({ variant: 'secondary', size: 'sm' })}>
            {t('pricing.footerNav.seeFeatures')} <CtaArrow />
          </Link>
        </div>
      </section>
    </main>
  );
}
